import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Box,
  useTheme,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useAuth } from "../../../../context";
import useDelete from "../../../../api/useDelete";
import SkeletonLoading from "./loading/SkeletonLoading";
import UpdateLaboratoryTypeDialog from "./UpdateLaboratoryTypeDialog";
import { useTranslation } from "react-i18next";

const ListLaboratoryTypes = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  const { data, isLoading, isError, error, refetch } = useFetchObjects(
    ["laboratory-tests"],
    `laboratory-tests/?search=${searchTerm}`,
    token
  );

  useEffect(() => {
    refetch();
  }, [searchTerm]);

  const { handleDelete, ConfirmDialog } = useDelete("laboratory-tests", token);

  // Open dialog for editing
  const handleEdit = (type) => {
    setSelectedType(type);
    setOpenDialog(true);
  };

  // Close the dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedType(null);
  };

  return (
    <Paper
      sx={{
        p: { xs: 2, sm: 4 },
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
        borderRadius: 3,
        width: "100%",
        overflowX: "auto",
      }}
    >
      <Typography
        variant={isSmDown ? "h6" : "h5"}
        gutterBottom
        fontWeight="bold"
        color={theme.palette.primary.main}
      >
        {t("laboratory.listLabTestTypes")}
      </Typography>

      {/* Search bar */}
      <TextField
        label={t("laboratory.search")}
        fullWidth
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
        }}
        placeholder={t("laboratory.searchByTestDescription")}
        size={isSmDown ? "small" : "medium"}
      />

      {/* Loading */}
      {isLoading && <SkeletonLoading />}

      {/* Error */}
      {isError && (
        <Typography color="error" sx={{ mb: 2 }}>
          {t("laboratory.error")}: {error.message}
        </Typography>
      )}

      {/* Table */}
      {data && (
        <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
          <Table
            sx={{ minWidth: 650 }}
            aria-label="laboratory types table"
            size={isSmDown ? "small" : "medium"}
          >
            <TableHead
              sx={{
                backgroundColor: theme.palette.grey[200],
                "& th": { fontWeight: "bold", textAlign: "center" },
              }}
            >
              <TableRow>
                <TableCell sx={{ minWidth: 140 }}>
                  {t("laboratory.testName")}
                </TableCell>
                <TableCell sx={{ minWidth: 250 }}>
                  {t("laboratory.description")}
                </TableCell>
                <TableCell sx={{ minWidth: 140 }}>
                  {t("laboratory.category")}
                </TableCell>
                <TableCell sx={{ minWidth: 140 }}>
                  {t("laboratory.parentTest")}
                </TableCell>
                <TableCell sx={{ minWidth: 100 }}>
                  {t("laboratory.cost")}
                </TableCell>
                <TableCell sx={{ minWidth: 100 }}>
                  {t("laboratory.units")}
                </TableCell>
                <TableCell sx={{ minWidth: 200 }}>
                  {t("laboratory.referenceRange") || "Reference Range"}
                </TableCell>
                <TableCell sx={{ minWidth: 100 }}>
                  {t("laboratory.action")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ bgcolor: "#f0fcfc" }}>
              {data.length === 0 && !isLoading && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                    {t("laboratory.noLabTestTypesFound")}
                  </TableCell>
                </TableRow>
              )}
              {data.map((type) => (
                <TableRow
                  key={type.id}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    "&:hover": { backgroundColor: "#e0f7f7" },
                    transition: "background-color 0.3s ease",
                  }}
                >
                  <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                    {type.name}
                  </TableCell>

                  {/* Description: scrollable box with max height and ellipsis */}
                  <TableCell
                    align="left"
                    sx={{ maxWidth: 300, position: "relative" }}
                  >
                    {type.description ? (
                      <Tooltip title={type.description} arrow>
                        <Box
                          sx={{
                            maxHeight: 56,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "normal",
                            lineHeight: 1.3,
                            cursor: "help",
                            paddingRight: 1,
                          }}
                        >
                          {type.description}
                        </Box>
                      </Tooltip>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontStyle="italic"
                      >
                        {t("laboratory.noDescription")}
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                    {type.category_display?.name || (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontStyle="italic"
                      >
                        {t("laboratory.noCategory")}
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                    {type.parent_test_display?.name || (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontStyle="italic"
                      >
                        {t("laboratory.noParentTest")}
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                    {type.cost}
                  </TableCell>

                  <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                    {type.units}
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      whiteSpace: "normal",
                      maxWidth: 200,
                      wordBreak: "break-word",
                    }}
                  >
                    {type.reference_range || (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontStyle="italic"
                      >
                        -
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                    <Tooltip title={t("laboratory.edit")} arrow>
                      <IconButton
                        color="primary"
                        onClick={() => handleEdit(type)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title={t("laboratory.delete")} arrow>
                      <IconButton
                        color="error"
                        onClick={() => handleDelete(type.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ConfirmDialog />

      {/* Update Dialog */}
      {selectedType && (
        <UpdateLaboratoryTypeDialog
          open={openDialog}
          onClose={handleCloseDialog}
          type={selectedType}
          token={token}
          refetch={refetch}
        />
      )}
    </Paper>
  );
};

export default ListLaboratoryTypes;
